<template lang="pug">
  .expand-text(
    :class="expandTextClass"
  )
    .expand-text__wrapper(
      :style="contentStyle"
      :class="contentClass"
    )
      span.expand-text__content(
        ref="content"
        v-html="description"
      )
    button.expand-text__button(
      v-if="isMaybeExpanded"
      @click="onClickButton"
    ) {{ buttonText }}
</template>

<script lang="ts">
  import type { PropType } from 'vue';

  type LineClamp = {
    [key: number]: number;
  };

  export default defineNuxtComponent({
    name: 'ExpandText',

    props: {
      lineClamps: {
        type: Object as PropType<LineClamp>,
        default: () => ({
          0: 5,
        }),
      },
      textExpanded: {
        type: String as PropType<string>,
        default: 'Скрыть',
      },
      textCollapsed: {
        type: String as PropType<string>,
        default: 'Ещё',
      },
      description: {
        type: String as PropType<string>,
        default: '',
      },
    },

    data: () => ({
      isCollapsed: true,
      isMounted: false,
      mutationObserver: null as null | MutationObserver,
      linesCount: 0,
      windowWidth: 0,
    }),

    computed: {
      contentClass(): any {
        return {
          'expand-text__wrapper--collapsed': this.isCollapsed,
          'expand-text__wrapper--expanded': !this.isCollapsed,
        };
      },

      expandTextClass(): any {
        return {
          'expand-text--mounted': this.isMounted,
        };
      },

      contentStyle(): any {
        return {
          '--expand-text-line-clamp': this.lineClampCurrent,
        };
      },

      buttonText(): string {
        const { textExpanded, textCollapsed } = this;

        return this.isCollapsed ? textCollapsed : textExpanded;
      },

      isMaybeExpanded(): boolean {
        return Boolean(this.linesCount > this.lineClampCurrent);
      },

      lineClampCurrent() {
        const { lineClamps, windowWidth } = this;

        const currentBreakpoint = Math.max(
          ...Object.keys(lineClamps)
            .map((breakpoint: string) => parseInt(breakpoint))
            .filter((breakpoint: number) => breakpoint <= windowWidth),
        );

        // @ts-ignore
        return this.lineClamps[currentBreakpoint];
      },
    },

    watch: {
      isCollapsed(newValue) {
        this.$emit(newValue ? 'collapse-text' : 'expand-text');
      },
    },

    mounted() {
      this.calcIsCollapsed();
      this.calcLinesCount();
      this.windowResizeCallback();

      window.addEventListener('resize', this.windowResizeCallback);

      this.mutationObserver = new MutationObserver(this.mutationObsserverCallback);
      this.mutationObserver.observe(this.$refs.content as HTMLElement, {
        characterData: true,
        subtree: true,
      });

      this.isMounted = true;
    },

    unmounted() {
      this.mutationObserver?.disconnect();
      window.removeEventListener('resize', this.windowResizeCallback);
    },

    methods: {
      calcLinesCount() {
        const content = this.$refs.content as HTMLElement;
        const contentRects = content.getClientRects();
        this.linesCount = Object.values(contentRects).reduce((result, rect, index, rects) => {
          let counter = 1;

          if (index) {
            const rectPrev = rects[index - 1];
            counter = rect.y !== rectPrev.y ? 1 : 0;
          }

          return result + counter;
        }, 0);
      },

      async calcIsCollapsed() {
        await this.$nextTick();

        const content = this.$refs.content as HTMLElement;

        // если элемент не отображается
        if (content.scrollHeight === 0) {
          return;
        }

        // -5 из-за того что при вставке ссылки scrollHeight немного больше
        this.isCollapsed = content.scrollHeight - 5 > content.offsetHeight;
      },

      mutationObsserverCallback(mutationsList: any, _observer: MutationObserver) {
        for (const mutation of mutationsList) {
          if (mutation.type === 'characterData') {
            this.calcIsCollapsed();
            this.calcLinesCount();
          }
        }
      },

      onClickButton() {
        this.isCollapsed = !this.isCollapsed;
      },

      windowResizeCallback() {
        this.windowWidth = window.innerWidth;
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use 'sass:color';

  .expand-text {
    opacity: 0;
    transition: opacity 0.3s;

    &--mounted {
      opacity: 1;
    }

    &__wrapper {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: var(--expand-text-line-clamp);
      word-break: break-word;

      &--expanded {
        -webkit-line-clamp: unset;
      }
    }

    &__button {
      border: none;
      background: none;
      outline: none;
      text-decoration: none;
      @include fontSmallText;
      color: #5c5c5c;

      @include hover {
        cursor: pointer;
        color: color.adjust(#5c5c5c, $lightness: -10%);
      }
    }
  }
</style>
